import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { apiFetch, isApiError } from '../api/client'
import type { SessionInfo } from '../api/types'

// 'loading'         — GET /auth/session in flight; render nothing data-bearing.
// 'unauthenticated' — no valid session (401 after one refresh attempt); show login.
// 'authenticated'   — session resolved; show the shell.
// 'error'           — gateway unreachable / 5xx; show a retryable error.
export type SessionStatus = 'loading' | 'unauthenticated' | 'authenticated' | 'error'

export interface SessionState {
  status: SessionStatus
  session: SessionInfo | null
  workspaceId: string | null
  recheck: () => void
  switchWorkspace: (workspaceId: string) => Promise<void>
  logout: () => Promise<void>
}

const SessionContext = createContext<SessionState | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [session, setSession] = useState<SessionInfo | null>(null)
  const [trigger, setTrigger] = useState(0)

  useEffect(() => {
    let cancelled = false
    apiFetch<SessionInfo>('/auth/session')
      .then((data) => {
        if (cancelled) return
        setSession(data)
        setStatus('authenticated')
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setSession(null)
        const s = isApiError(err) ? err.status : 0
        setStatus(s === 401 || s === 403 ? 'unauthenticated' : 'error')
      })
    return () => {
      cancelled = true
    }
  }, [trigger])

  const recheck = useCallback(() => {
    setStatus('loading')
    setSession(null)
    setTrigger((v) => v + 1)
  }, [])

  const switchWorkspace = useCallback(
    async (workspaceId: string) => {
      await apiFetch('/auth/active-workspace', {
        method: 'POST',
        body: JSON.stringify({ workspace_id: workspaceId }),
      })
      // Workspace scope is resolved server-side from the cookie, so every
      // cached tenant query is now stale.
      queryClient.clear()
      recheck()
    },
    [queryClient, recheck],
  )

  const logout = useCallback(async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' })
    } finally {
      queryClient.clear()
      setSession(null)
      setStatus('unauthenticated')
    }
  }, [queryClient])

  return (
    <SessionContext.Provider
      value={{
        status,
        session,
        workspaceId: session?.workspace.id ?? null,
        recheck,
        switchWorkspace,
        logout,
      }}
    >
      {children}
    </SessionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSession(): SessionState {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used within SessionProvider')
  return ctx
}
